import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { showProQuestSearch } from "config";

import { useSearchCredentials } from "../hooks";

const MASKED_VALUE = "••••••••";

const allProviders = [
  {
    credentialType: "ebsco",
    title: "EBSCO",
    description:
      "EBSCO is the leading provider of research databases, e-journals, magazine subscriptions, ebooks and discovery service for academic libraries, public libraries, corporations, schools, government and medical institutions",
  },
  {
    credentialType: "proQuest",
    title: "ProQuest",
    description:
      "Connect ProQuest to search its research database directly from Sooth.",
  },
];

const providers = allProviders.filter(
  (provider) => provider.credentialType !== "proQuest" || showProQuestSearch
);

function maskValue(value) {
  return value ? MASKED_VALUE : "Not provided";
}

function ProviderCredentialSection({
  provider,
  credential,
  isSaving,
  onSave,
}) {
  const [username, setUsername] = useState(credential.username);
  const [profile, setProfile] = useState(credential.profile);
  const [password, setPassword] = useState("");
  const [isEditing, setIsEditing] = useState(!credential.hasSavedCredential);
  const [error, setError] = useState(null);

  useEffect(() => {
    setUsername(credential.username);
    setProfile(credential.profile);
    if (!credential.hasSavedCredential) setIsEditing(true);
  }, [credential.username, credential.profile, credential.hasSavedCredential]);

  const fieldsLocked = credential.hasSavedCredential && !isEditing;
  const canSave =
    !fieldsLocked &&
    username.trim() &&
    !isSaving &&
    (credential.hasSavedCredential || password.trim());

  async function handleSave() {
    setError(null);
    const response = await onSave({
      credentialType: provider.credentialType,
      username: username.trim(),
      password: password.trim(),
      profile: profile.trim() || null,
      isEnabled: true,
    });

    if (response.success) {
      setPassword("");
      setIsEditing(false);
      toast.success(`${provider.title} credentials saved and verified.`);
    } else {
      setError(response.error);
    }
  }

  async function handleToggle(_event, enabled) {
    setError(null);
    const response = await onSave({
      credentialType: provider.credentialType,
      username: credential.username,
      password: "",
      profile: credential.profile || null,
      isEnabled: enabled,
    });

    if (response.success) {
      toast.success(
        `${provider.title} is now ${enabled ? "enabled" : "disabled"}.`
      );
    } else {
      setError(response.error);
    }
  }

  return (
    <Stack spacing={1.5} py={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography component="h2" variant="h4">
          {provider.title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography>Off</Typography>
          <Tooltip
            title={
              credential.hasSavedCredential
                ? "Enable or disable this search provider for the group."
                : `Save valid ${provider.title} credentials before enabling this provider.`
            }
          >
            <span>
              <Switch
                checked={credential.isEnabled}
                onChange={handleToggle}
                disabled={!credential.hasSavedCredential || isSaving}
              />
            </span>
          </Tooltip>
          <Typography>On</Typography>
        </Stack>
      </Stack>

      <Typography>{provider.description}</Typography>

      {fieldsLocked ? (
        <Stack spacing={0.5} maxWidth="sm">
          <Typography>
            <strong>Username:</strong> {maskValue(credential.username)}
          </Typography>
          <Typography>
            <strong>Password:</strong> {MASKED_VALUE}
          </Typography>
          <Typography>
            <strong>Profile / Account ID:</strong> {maskValue(credential.profile)}
          </Typography>
          <Box>
            <Tooltip title={`Edit ${provider.title} credentials`}>
              <IconButton
                aria-label={`Edit ${provider.title} credentials`}
                onClick={() => setIsEditing(true)}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1.5} maxWidth="sm">
          <Typography>
            Enter your credentials. The password may be left blank when editing
            if it has not changed.
          </Typography>
          <TextField
            label={`${provider.title} Username`}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={isSaving}
            required
            fullWidth
          />
          <TextField
            label={`${provider.title} Password`}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={credential.hasSavedCredential ? MASKED_VALUE : ""}
            helperText={
              credential.hasSavedCredential
                ? "Leave blank to keep the current password."
                : ""
            }
            disabled={isSaving}
            required={!credential.hasSavedCredential}
            fullWidth
          />
          <TextField
            label={`${provider.title} Profile / Account ID`}
            value={profile}
            onChange={(event) => setProfile(event.target.value)}
            disabled={isSaving}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Button type="button" onClick={handleSave} disabled={!canSave}>
              {isSaving ? "Verifying..." : "Save and Verify"}
            </Button>
          </Box>
        </Stack>
      )}

      {provider.credentialType === "ebsco" && (
        <Typography>
          Don't have EBSCO? <Link to="/profile/support">Visit our Support page</Link>{" "}
          to request access.
        </Typography>
      )}
    </Stack>
  );
}

export default function DatabaseIntegration() {
  const { credentials, isLoading, savingType, saveCredential } =
    useSearchCredentials();

  if (isLoading) {
    return <CircularProgress aria-label="Loading search credentials" />;
  }

  return (
    <Stack pl={3}>
      {providers.map((provider, index) => (
        <div key={provider.credentialType}>
          {index > 0 && <Divider />}
          <ProviderCredentialSection
            provider={provider}
            credential={credentials[provider.credentialType]}
            isSaving={savingType === provider.credentialType}
            onSave={saveCredential}
          />
        </div>
      ))}
    </Stack>
  );
}
